import GermanDateInput from "@/components/GermanDateInput";
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function EntryDialog({ open, onOpenChange, coachUserId, coaches, isAdmin, currentUserId, onSaved, initialData }) {
  const [form, setForm] = useState({
    coach_user_id: coachUserId || currentUserId,
    date: todayIso(),
    hours: "",
    ue: "",
    customers: "",
    client_name: "",
    time_from: "",
    time_to: "",
    notes: "",
    section_key: "",   // "" | "clientId::sectionId"
  });
  const [autoUe, setAutoUe] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectionOptions, setSectionOptions] = useState([]);
  const editing = !!initialData?.entry_id;

  useEffect(() => {
    if (!open) return;
    api.get("/me/section-options").then((r) => setSectionOptions(r.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) {
      if (initialData?.entry_id) {
        setForm({
          coach_user_id: initialData.coach_user_id,
          date: initialData.date || todayIso(),
          hours: initialData.hours ?? "",
          ue: initialData.ue ?? "",
          customers: initialData.customers ?? "",
          client_name: initialData.client_name || "",
          time_from: initialData.time_from || "",
          time_to: initialData.time_to || "",
          notes: initialData.notes || "",
          section_key: initialData.client_id && initialData.section_id
            ? `${initialData.client_id}::${initialData.section_id}` : "",
        });
        setAutoUe(false);
      } else {
        setForm({
          coach_user_id: initialData?.coach_user_id || coachUserId || currentUserId,
          date: initialData?.date || todayIso(),
          hours: "",
          ue: "",
          customers: "",
          client_name: "",
          time_from: "",
          time_to: "",
          notes: "",
          section_key: "",
        });
        setAutoUe(true);
      }
    }
  }, [open, initialData, coachUserId, currentUserId]);

  const handleHours = (v) => {
    setForm((f) => {
      const next = { ...f, hours: v };
      if (autoUe && v !== "" && !Number.isNaN(parseFloat(v))) {
        next.ue = (parseFloat(v) * 2).toString();
      }
      return next;
    });
  };

  const handleUe = (v) => {
    setAutoUe(false);
    setForm((f) => ({ ...f, ue: v }));
  };

  const submit = async () => {
    if (!form.date || form.hours === "" || form.ue === "" || form.customers === "") {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    setSaving(true);
    const [cid, sid] = (form.section_key || "").split("::");
    const payload = {
      coach_user_id: form.coach_user_id,
      date: form.date,
      hours: parseFloat(form.hours),
      ue: parseFloat(form.ue),
      customers: parseInt(form.customers, 10),
      client_name: form.client_name || null,
      time_from: form.time_from || null,
      time_to: form.time_to || null,
      notes: form.notes || null,
      client_id: cid || null,
      section_id: sid || null,
    };
    try {
      if (editing) {
        await api.put(`/entries/${initialData.entry_id}`, payload);
        toast.success("Eintrag aktualisiert");
      } else {
        await api.post("/entries", payload);
        toast.success("Eintrag gespeichert");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-none border border-slate-200 p-0" data-testid="entry-dialog">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
            {editing ? "Eintrag bearbeiten" : "Neuer Eintrag"}
          </div>
          <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
            Coaching-Stunden erfassen
          </DialogTitle>
          <DialogDescription className="sr-only">
            Erfasse Zeitstunden, UE und Anzahl der Kunden für einen Coaching-Tag.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 space-y-5 max-h-[60vh] overflow-y-auto">
          {isAdmin && (
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Coach</label>
              <select
                data-testid="entry-coach-select"
                className="input-tech"
                value={form.coach_user_id}
                onChange={(e) => setForm({ ...form, coach_user_id: e.target.value })}
              >
                {coaches?.map((c) => (
                  <option key={c.user_id} value={c.user_id}>{`${c.name} (${c.email})`}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Datum</label>
            <GermanDateInput
              data-testid="entry-date-input"
              
              className="input-tech font-mono"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Zeitstunden</label>
              <input
                data-testid="entry-hours-input"
                type="number"
                step="0.25"
                min="0"
                className="input-tech font-mono"
                value={form.hours}
                onChange={(e) => handleHours(e.target.value)}
                placeholder="2.0"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                UE (auto: 2× h)
              </label>
              <input
                data-testid="entry-ue-input"
                type="number"
                step="0.5"
                min="0"
                className="input-tech font-mono"
                value={form.ue}
                onChange={(e) => handleUe(e.target.value)}
                placeholder="4.0"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kunden</label>
              <input
                data-testid="entry-customers-input"
                type="number"
                min="0"
                className="input-tech font-mono"
                value={form.customers}
                onChange={(e) => setForm({ ...form, customers: e.target.value })}
                placeholder="1"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Von</label>
              <input
                data-testid="entry-from-input"
                type="time"
                className="input-tech font-mono"
                value={form.time_from}
                onChange={(e) => setForm({ ...form, time_from: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Bis</label>
              <input
                data-testid="entry-to-input"
                type="time"
                className="input-tech font-mono"
                value={form.time_to}
                onChange={(e) => setForm({ ...form, time_to: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
              AVGS-Abschnitt (optional · zählt UE automatisch hoch)
            </label>
            <select
              data-testid="entry-section-select"
              className="input-tech"
              value={form.section_key}
              onChange={(e) => setForm({ ...form, section_key: e.target.value })}
            >
              <option value="">— kein Abschnitt (nur Coach-Zeit) —</option>
              {sectionOptions.map((s) => (
                <option key={`${s.client_id}::${s.section_id}`} value={`${s.client_id}::${s.section_id}`}>
                  {s.client_name} · {s.module_name ? `${s.module_name} · ` : ""}{s.section_name} ({s.ue_completed}/{s.ue_target} UE)
                </option>
              ))}
            </select>
            {sectionOptions.length === 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                Du hast noch keine AVGS-Abschnitte zugewiesen bekommen.
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kunde / Slot (optional)</label>
            <input
              data-testid="entry-client-input"
              type="text"
              className="input-tech"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="z. B. Max Mustermann"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Notizen</label>
            <input
              data-testid="entry-notes-input"
              type="text"
              className="input-tech"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional"
            />
          </div>
        </div>

        <DialogFooter className="px-8 py-5 border-t border-slate-100 flex gap-3">
          <button
            data-testid="entry-cancel-button"
            onClick={() => onOpenChange(false)}
            className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            data-testid="entry-save-button"
            onClick={submit}
            disabled={saving}
            className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {saving ? "Speichert…" : editing ? "Aktualisieren" : "Speichern"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
